
      import { createComponent } from '@lit/react'
      import { type EventName } from '@lit/react'
      import * as React from 'react'

      import Component from '../../components/timepicker/timepicker.component.js'
      import type { DdsChangeEvent } from '../../events/events'
import type { DdsInputEvent } from '../../events/events'
import type { DdsValidTimeEvent } from '../../events/events'
import type { DdsInvalidTimeEvent } from '../../events/events'
import type { DdsClearEvent } from '../../events/events'
import type { DdsFocusEvent } from '../../events/events'
import type { DdsBlurEvent } from '../../events/events'
      export type { DdsChangeEvent } from '../../events/events'
export type { DdsInputEvent } from '../../events/events'
export type { DdsValidTimeEvent } from '../../events/events'
export type { DdsInvalidTimeEvent } from '../../events/events'
export type { DdsClearEvent } from '../../events/events'
export type { DdsFocusEvent } from '../../events/events'
export type { DdsBlurEvent } from '../../events/events'

      const tagName = 'dap-ds-timepicker'

      /**
 * `dap-ds-timepicker`
 * @summary A timepicker is a graphical user interface widget that allows the user to select a time from a time range.
 * @element dap-ds-timepicker
 * @title - Timepicker
 *
 * @property {Dayjs} value - The value of the timepicker.
 * @property {string} label - The label of the timepicker.
 * @property {string} description - The description of the timepicker.
 * @property {'xs' | 'sm' | 'lg'} size - The size of the timepicker.
 * @property {boolean} disabled - The disabled state of the timepicker.
 * @property {boolean} required - The required state of the timepicker.
 * @property {boolean} readonly - The readonly state of the timepicker.
 * @property {boolean} autofocus - The autofocus state of the timepicker.
 * @property {string} tooltip - The tooltip of the timepicker.
 * @property {'top' | 'right' | 'bottom' | 'left'} tooltipPlacement - The tooltip placement of the timepicker.
 * @property {string} feedback - The feedback of the timepicker.
 * @property {'negative' | 'positive' | 'warning'} feedbackType - The feedback type of the timepicker.
 * @property {boolean} optional - The optional state of the timepicker.
 * @property {string} optionalLabel - The optional label of the timepicker.
 * @property {boolean} subtle - The weight of the label. Default is `false`
 * @property {string} autocomplete - The autocomplete of the timepicker.
 * @property {string} placeholder - The placeholder of the timepicker.
 * @property {boolean} loading - The loading state of the timepicker.
 * @property {string} format - The format of the timepicker. Defaults to 'HH:mm'.
 * @property {string} minTime - The minimum time of the timepicker. Format: 'HH:mm'.
 * @property {string} maxTime - The maximum time of the timepicker. Format: 'HH:mm'.
 * @property {number} step - The step interval in minutes. Defaults to 15.
 * @property {boolean} openTimeOnInput - Whether the time picker should open on typing.
 * @property {boolean} sendEmptyEventOnInput - Whether the time picker should send an empty event on typing.
 * @property {string} clearButton - Whether the clear button should be shown. Defaults to 'true'.
 * @property {string} clearButtonAriaLabel - The aria label of the clear button.
 * @property {string} placement - The placement of the dropdown of the timepicker.
 * @property {FloatingStrategy} floatingStrategy - The floating strategy of the timepicker.
 * @property {string} locale - The locale of the timepicker.
 * @property {boolean} closeOnSelection - Whether the dropdown should close when a time is selected.
 * @property {boolean} showSeconds - Whether to show the seconds column in the time picker.
 * @property {number} secondsStep - The step interval in seconds. Defaults to 1.
 * @property {function} disabledTime - Callback to determine if a time should be disabled.
 * @property {boolean} hideDisabledTimes - Whether to hide disabled times or show them as disabled.
 * @property {TimePreset[]} presets - Array of preset time slots for quick selection.
 *
 * @event {{ value: string }} dds-change - Fired when the timepicker value changes.
 * @event {{ value: string }} dds-input - Fired when the timepicker input value changes.
 * @event {{ value: string }} dds-valid-time - Fired when the timepicker input value is valid. Happens on manual input typing.
 * @event {{ value: string, type: 'invalid' | 'out-of-range' }} dds-invalid-time - Fired when the timepicker input value is invalid. Happens on manual input typing.
 * @event {{ void }} dds-clear - Fired when the timepicker is cleared.
 * @event {{ void }} dds-focus - Emitted when the timepicker gains focus.
 * @event {{ void }} dds-blur - Emitted when the timepicker loses focus.
 *
 * @csspart base - The main timepicker container.
 * @csspart trigger - The trigger button of the timepicker.
 * @csspart label - The label of the timepicker.
 * @csspart description - The description of the timepicker.
 * @csspart feedback - The feedback of the timepicker.
 * @csspart tooltip - The tooltip of the timepicker.
 * @csspart input - The input of the timepicker.
 * @csspart clear-button - The clear button of the timepicker.
 *
 * @cssproperty --dds-timepicker-background - The background color of the timepicker. Defaults to var(--dds-fields-background-default).
 * @cssproperty --dds-timepicker-border-color - The border color of the timepicker. Defaults to var(--dds-border-neutral-base).
 * @cssproperty --dds-timepicker-border-width - The border width of the timepicker. Defaults to var(--dds-border-width-base, 1px).
 * @cssproperty --dds-timepicker-border-radius - The border radius of the timepicker. Defaults to var(--dds-radius-base).
 * @cssproperty --dds-timepicker-text-color - The text color of the timepicker. Defaults to var(--dds-text-neutral-base).
 * @cssproperty --dds-timepicker-placeholder-color - The placeholder text color. Defaults to var(--dds-text-neutral-subtle).
 * @cssproperty --dds-timepicker-disabled-background - The background color when disabled. Defaults to var(--dds-background-neutral-stronger).
 * @cssproperty --dds-timepicker-disabled-text - The text color when disabled. Defaults to var(--dds-text-neutral-disabled).
 * @cssproperty --dds-timepicker-error-border - The border color for error state. Defaults to var(--dds-border-negative-base).
 * @cssproperty --dds-timepicker-success-border - The border color for success state. Defaults to var(--dds-border-positive-base).
 * @cssproperty --dds-timepicker-icon-color - The color of the icons. Defaults to var(--dds-text-icon-neutral-subtle).
 * @cssproperty --dds-timepicker-clear-icon-color - The color of the clear icon. Defaults to var(--dds-button-subtle-icon-neutral-enabled).
 * @cssproperty --dds-timepicker-popup-background - The background color of the popup. Defaults to var(--dds-background-neutral-subtle).
 * @cssproperty --dds-timepicker-popup-shadow - The box shadow of the popup. Defaults to 0 4px 6px -1px rgb(0 0 0 / 8%), 0 2px 4px -1px rgb(0 0 0 / 6%).
 *
 * @cssproperty --dds-timepicker-padding-xs - The padding for extra small size. Defaults to var(--dds-spacing-200).
 * @cssproperty --dds-timepicker-padding-sm - The padding for small size. Defaults to var(--dds-spacing-200).
 * @cssproperty --dds-timepicker-padding-lg - The padding for large size. Defaults to var(--dds-spacing-300).
 * @cssproperty --dds-timepicker-padding-horizontal - The horizontal padding. Defaults to var(--dds-spacing-300).
 * @cssproperty --dds-timepicker-padding-vertical - The vertical padding. Defaults to var(--dds-spacing-200).
 * @cssproperty --dds-timepicker-gap - The gap between elements. Defaults to var(--dds-spacing-100).
 * @cssproperty --dds-timepicker-icon-gap - The gap between icons. Defaults to var(--dds-spacing-200).
 * @cssproperty --dds-timepicker-action-gap - The gap between action elements. Defaults to var(--dds-spacing-200).
 * @cssproperty --dds-timepicker-action-padding - The padding for action elements. Defaults to var(--dds-spacing-300).
 * @cssproperty --dds-timepicker-clear-icon-width - The width of the clear icon. Defaults to var(--dds-spacing-800).
 * @cssproperty --dds-timepicker-dropdown-icon-right - The right position of the dropdown icon. Defaults to var(--dds-spacing-600).
 * @cssproperty --dds-timepicker-min-width - The minimum width of the timepicker. Defaults to 7.5rem.
 *
 */
      const reactWrapper = createComponent({
        tagName,
        elementClass: Component,
        react: React,
        events: {
          onDdsChange: 'dds-change' as EventName<DdsChangeEvent>,
onDdsInput: 'dds-input' as EventName<DdsInputEvent>,
onDdsValidTime: 'dds-valid-time' as EventName<DdsValidTimeEvent>,
onDdsInvalidTime: 'dds-invalid-time' as EventName<DdsInvalidTimeEvent>,
onDdsClear: 'dds-clear' as EventName<DdsClearEvent>,
onDdsFocus: 'dds-focus' as EventName<DdsFocusEvent>,
onDdsBlur: 'dds-blur' as EventName<DdsBlurEvent>
        },
        displayName: "DapDSTimePicker"
      })

      export default reactWrapper
    